import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SortIcon from "@mui/icons-material/Sort";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import {
  branchList,
  deleteBranch,
  updateBranch,
} from "../../../Services/Services";
import AddBranchForm from "./AddNewBranch"; // Update the path if necessary
import Popover from "@mui/material/Popover";

const BranchList = () => {
  const [branches, setBranches] = useState([]);
  const [editBranchIndex, setEditBranchIndex] = useState(null);
  const [editBranchData, setEditBranchData] = useState({});
  const [openEditModal, setOpenEditModal] = useState(false);
  const [filter, setFilter] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [branchContactError, setBranchContactError] = useState("");

  const [openAddModal, setOpenAddModal] = useState(false);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [branchToDelete, setBranchToDelete] = useState(null);

  useEffect(() => {
    const branchListApi = async () => {
      try {
        const response = await branchList();
        setBranches(response);
      } catch (error) {}
    };

    branchListApi();
  }, []);

  const getFilteredSortedBranches = () => {
    let filtered = branches.filter(
      (branch) =>
        branch.branch_name?.toLowerCase().includes(filter.toLowerCase()) ||
        branch.branch_address?.toLowerCase().includes(filter.toLowerCase())
    );

    filtered.sort((a, b) => {
      const nameA = a.branch_name?.toLowerCase() || "";
      const nameB = b.branch_name?.toLowerCase() || "";
      return sortAsc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });

    return filtered;
  };

  const handleDelete = async (branch_id) => {
    try {
      const response = await deleteBranch(branch_id); // don't pass any argument if not needed
      setBranches(response);
    } catch (error) {
      console.error("Error fetching branch:", error);
    }
  };

  const handleEdit = (branch_id) => {
    const branchToEdit = branches.find(
      (branch) => branch.branch_id === branch_id
    );
    setEditBranchData({ ...branchToEdit });
    setEditBranchIndex(branch_id);
    setOpenEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    if (name === "branchContactNo") {
      const onlyNumbers = value.replace(/\D/g, "").slice(0, 10);
      setEditBranchData((prev) => ({ ...prev, [name]: onlyNumbers }));
      setBranchContactError("");
    } else {
      setEditBranchData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveEdit = async () => {
    if ((editBranchData.branch_contact || "").length !== 10) {
      setBranchContactError("Branch Contact No must be exactly 10 digits");
      return;
    }

    setBranchContactError("");

    try {
      const response = await updateBranch(editBranchIndex, editBranchData); // don't pass any argument if not needed
      setBranches(response);
      setOpenEditModal(false);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
    setOpenEditModal(false);
  };

  const handleCancelEdit = () => {
    setOpenEditModal(false);
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteBranch(branchToDelete);
      setBranches(response);
    } catch (error) {
      console.error("Error deleting branch:", error);
    } finally {
      setOpenDeleteDialog(false);
      setBranchToDelete(null);
    }
  };

const handleDownloadExcel = () => {
  const tableElement = document.getElementById("branch-table-print");
  if (!tableElement) {
    alert("Table not found");
    return;
  }

  const rows = tableElement.querySelectorAll("tbody tr");
  if (!rows.length) {
    alert("No data available to download.");
    return;
  }

  // Get headers and find index of 'Action' column
  const allHeaders = Array.from(tableElement.querySelectorAll("thead th")).map(
    (th) => th.innerText.trim()
  );

  const headers = allHeaders.filter((header) => header.toLowerCase() !== "actions");
  const actionColIndex = allHeaders.findIndex((header) => header.toLowerCase() === "actions");

  // Filter row data excluding 'Action' column
  const filteredData = Array.from(rows).map((row, i) => {
    const cells = row.querySelectorAll("td");
    const rowData = { "S. No.": i + 1 };

    headers.forEach((header, idx) => {
      let adjustedIndex = idx;
      if (actionColIndex !== -1 && idx >= actionColIndex) {
        adjustedIndex += 1; // Skip the action column
      }
      rowData[header] = cells[adjustedIndex]?.innerText.trim();
    });

    return rowData;
  });

  const worksheetData = [
    Object.keys(filteredData[0]),
    ...filteredData.map((obj) => Object.values(obj)),
  ];
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // Auto column width
  worksheet["!cols"] = Object.keys(filteredData[0]).map((_, colIndex) => {
    const maxLength = worksheetData.reduce((max, row) => {
      const cell = row[colIndex];
      const len = cell ? cell.toString().length : 10;
      return Math.max(max, len);
    }, 0);
    return { wch: maxLength + 2 };
  });

  // Styling
  const blackBorder = {
    top: { style: "thin", color: { rgb: "000000" } },
    bottom: { style: "thin", color: { rgb: "000000" } },
    left: { style: "thin", color: { rgb: "000000" } },
    right: { style: "thin", color: { rgb: "000000" } },
  };

  worksheetData[0].forEach((_, colIdx) => {
    const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: colIdx })];
    if (cell) {
      cell.s = {
        fill: { fgColor: { rgb: "FFFF99" } },
        font: { bold: true, color: { rgb: "000000" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: blackBorder,
      };
    }
  });

  for (let r = 1; r < worksheetData.length; r++) {
    for (let c = 0; c < worksheetData[0].length; c++) {
      const cell = worksheet[XLSX.utils.encode_cell({ r, c })];
      if (cell) {
        cell.s = {
          alignment: { horizontal: "center", vertical: "center" },
          border: blackBorder,
        };
      }
    }
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Branch List");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const data = new Blob([excelBuffer], {
    type: "application/octet-stream",
  });

  saveAs(data, "BranchList.xlsx");
};


  const handlePrint = () => {
    const printContent = document.getElementById("branch-table-print");
    const WinPrint = window.open("", "", "width=900,height=650");

    WinPrint.document.write(`
    <html>
      <head>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 0px 20px 0px 20px;
            position: relative;
          }
          header {
            text-align: center;
            font-size: 20px;
            font-weight: bold;
            margin-bottom: 20px;
            color: #333;
          }
          footer {
            position: fixed;
            bottom: 10px;
            width: 100%;
            text-align: center;
            font-size: 12px;
            color: #333;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid black;
            padding: 8px;
            text-align: center;
            font-size: 12px;
          }
          th {
            background-color: rgba(211, 211, 211, 0.42);
            color: #000;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print {
            display: none;
          }
        </style>
      </head>
      <body>
        <header>SR ENTERPRICES</header>
        <h3 style="text-align: center;">Branch List</h3>
        ${printContent.innerHTML}
        <footer>Powered by : YASHVITECH IT SOLUTION PVT LTD</footer>
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
    </html>
  `);

    WinPrint.document.close();
    WinPrint.focus();
  };

  return (
    <div style={{ width: "100%", maxHeight: "calc(100vh - 70px)" }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          py: 1,
          mb: 2,
          position: "sticky",
          top: 10,
          zIndex: 1000,
        }}
      >
        <TextField
          label="Search by branch name or address"
          variant="outlined"
          size="small"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ minWidth: 260,marginTop: -4 }}
        />

        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            border: "1px solid #ccc",
            px: 2,
            py: 1,
            borderRadius: "8px",
            whiteSpace: "nowrap",
            minWidth: 220,
            marginTop: -4,
            maxHeight:"38px"
          }}
        >
          Total Branches: {getFilteredSortedBranches().length}
        </Typography>

        <Button
          variant="outlined"
          sx={{ minWidth: 300,marginTop: -4 ,maxHeight:"38px"}}
          startIcon={<SortIcon />}
          onClick={() => setSortAsc(!sortAsc)}
        >
          Sort by Name ({sortAsc ? "A-Z" : "Z-A"})
        </Button>
        <Button
          variant="contained"
           sx={{ marginTop: -4,maxHeight:"38px" }}
          color="primary"
          onClick={() => setOpenAddModal(true)}
        >
          Add New Branch
        </Button>
        <Button
          variant="outlined"
          onClick={handleDownloadExcel}
          sx={{ minWidth: "40px", padding: 0.2 ,marginTop: -4,maxHeight:"38px"}}
        >
          <FileDownloadIcon fontSize="small" />
        </Button>

        <Button
          variant="outlined"
          onClick={handlePrint}
          sx={{ minWidth: "40px", padding: 0.8,maxHeight:"38px" ,marginTop: -4}}
        >
          <PrintIcon fontSize="small" />
        </Button>
      </Box>

      {branches.length === 0 ? (
        <Typography>No branches found.</Typography>
      ) : (
        <div id="branch-table-print">
          <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
            <div style={{ maxHeight: "75vh", overflowX: "auto" }}>
              <Table
                stickyHeader
                size="small"
                sx={{ "& td, & th": { borderBottom: "1px solid #ccc" } }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": {
                        fontWeight: "bold",
                        color: "#333",
                        borderBottom: "1px solid #999",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell align="center">S. No.</TableCell>
                    <TableCell align="center">Branch ID</TableCell>
                    <TableCell align="center">Branch Name</TableCell>
                    <TableCell align="center">Branch Address</TableCell>
                    <TableCell align="center">Branch Contact No</TableCell>
                    <TableCell align="center" className="no-print">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredSortedBranches().map((branch, index) => {
                    return (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#fafafa" : "#ffffff", // alternate row color
                          "& td": {
                            paddingTop: "0px",
                            paddingBottom: "0px",
                            fontSize: "0.8125rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 120,
                            color: (theme) =>
                              theme.palette.mode === "dark"
                                ? "white"
                                : "inherit",
                            backgroundColor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "black"
                                : "inherit",
                          },
                        }}
                      >
                        <TableCell align="center">{index + 1}</TableCell>
                        <TableCell align="center">{branch.branch_id}</TableCell>
                        <TableCell align="center">
                          {branch.branch_name}
                        </TableCell>
                        <TableCell align="center">
                          {branch.branch_address}
                        </TableCell>
                        <TableCell align="center">
                          {branch.branch_contact}
                        </TableCell>
                        <TableCell align="center" className="no-print">
                          <IconButton
                            onClick={() => handleEdit(branch.branch_id)}
                            color="primary"
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>

                          <IconButton
                            onClick={(e) => {
                              setAnchorEl(e.currentTarget);
                              setBranchToDelete(branch.branch_id);
                            }}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>

                          {/* <IconButton onClick={() => handleDelete(branch.branch_id)} color="error" size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton> */}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </TableContainer>
        </div>
      )}

      {/* Edit Branch Modal */}
      <Dialog open={openEditModal} onClose={handleCancelEdit}>
        <DialogTitle>Edit Branch</DialogTitle>
        <DialogContent>
          <TextField
            label="Branch Name"
            variant="outlined"
            fullWidth
            margin="normal"
            name="branch_name"
            value={editBranchData.branch_name || ""}
            onChange={handleEditChange}
          />
          <TextField
            label="Branch Address"
            variant="outlined"
            fullWidth
            margin="normal"
            name="branch_address"
            value={editBranchData.branch_address || ""}
            onChange={handleEditChange}
            multiline
            rows={2}
          />
          <TextField
            label="Branch Contact No"
            variant="outlined"
            fullWidth
            margin="normal"
            name="branch_contact"
            type="tel"
            inputProps={{ inputMode: "numeric", maxLength: 10 }}
            value={editBranchData.branch_contact || ""}
            onChange={handleEditChange}
            error={!!branchContactError}
            helperText={branchContactError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} color="error">
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Branch</DialogTitle>
        <DialogContent dividers>
          <AddBranchForm
            setBranches={setBranches}
            setOpenAddModal={setOpenAddModal}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddModal(false)} color="error">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this branch?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="primary">
            No
          </Button>
          <Button onClick={confirmDelete} color="error">
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box sx={{ p: 1 }}>
          <Typography variant="body2">Are you sure?</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button
              color="error"
              size="small"
              onClick={async () => {
                try {
                  await handleDelete(branchToDelete);
                } finally {
                  setAnchorEl(null);
                  setBranchToDelete(null);
                }
              }}
            >
              Yes
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setBranchToDelete(null);
              }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Popover>
    </div>
  );
};

export default BranchList;
